// Progreso de reproducción: siempre en el navegador; además en Cloudflare KV si está activado.
export interface ProgressEntry {
  pos: number;
  dur: number;
  at: number;
  titleId: string;
  episodeId?: string;
}

const LS_KEY = "radiant.progress.v1";
let memory: Record<string, ProgressEntry> | null = null;
let syncEnabled = false;
let dirty: Record<string, ProgressEntry> = {};
let timer: ReturnType<typeof setTimeout> | null = null;
const listeners = new Set<() => void>();

function load(): Record<string, ProgressEntry> {
  if (memory) return memory;
  try {
    memory = JSON.parse(localStorage.getItem(LS_KEY) ?? "{}") as Record<string, ProgressEntry>;
  } catch {
    memory = {};
  }
  return memory;
}

function persist() {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(load()));
  } catch {
    /* almacenamiento lleno o bloqueado: se mantiene en memoria */
  }
  listeners.forEach((l) => l());
}

export const keyFor = (titleId: string, episodeId?: string) => (episodeId ? `${titleId}/${episodeId}` : titleId);

export function getProgress(titleId: string, episodeId?: string): ProgressEntry | undefined {
  return load()[keyFor(titleId, episodeId)];
}

export function setProgress(titleId: string, episodeId: string | undefined, pos: number, dur: number) {
  const e: ProgressEntry = { pos, dur, at: Date.now(), titleId, episodeId };
  load()[keyFor(titleId, episodeId)] = e;
  persist();
  if (syncEnabled) {
    dirty[keyFor(titleId, episodeId)] = e;
    timer ??= setTimeout(flush, 60_000);
  }
}

export function clearProgress(titleId: string, episodeId?: string) {
  delete load()[keyFor(titleId, episodeId)];
  persist();
}

/** Lo que se está viendo (sin terminar), ordenado por recencia: para «Continuar viendo». */
export function inProgress(): ProgressEntry[] {
  const byTitle = new Map<string, ProgressEntry>();
  for (const e of Object.values(load())) {
    if (e.dur > 0 && e.pos / e.dur > 0.93) continue;
    if (e.pos < 10) continue;
    const prev = byTitle.get(e.titleId);
    if (!prev || prev.at < e.at) byTitle.set(e.titleId, e);
  }
  return [...byTitle.values()].sort((a, b) => b.at - a.at);
}

export function lastWatched(titleId: string): ProgressEntry | undefined {
  return Object.values(load()).filter((e) => e.titleId === titleId).sort((a, b) => b.at - a.at)[0];
}

export function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export async function flush(): Promise<void> {
  timer = null;
  if (!syncEnabled || !Object.keys(dirty).length) return;
  const body = dirty;
  dirty = {};
  try {
    await fetch("/api/progress", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body), keepalive: true });
  } catch {
    dirty = { ...body, ...dirty };
  }
}

export async function enableSync(): Promise<void> {
  syncEnabled = true;
  try {
    const r = await fetch("/api/progress");
    if (!r.ok) return;
    const { entries } = (await r.json()) as { entries: Record<string, ProgressEntry> };
    const local = load();
    for (const [k, v] of Object.entries(entries)) if (!local[k] || local[k].at < v.at) local[k] = v;
    persist();
  } catch {
    /* sin conexión: se sincronizará más tarde */
  }
  addEventListener("pagehide", () => void flush());
}
